/**
 * Утилиты для парсинга состояния страницы и отправки агенту
 */
import { config } from '../config/runtimeConfig.js';
import { carTitle } from './carTitle';
import { deliveryCell } from './deliveryDate';
import { warrantyTermWithUnit } from './warrantyTerm';

/**
 * ID страниц, на которых работает SPA hash-навигация (каталог машин).
 * Используется в commandExecutor и commandGenerator для определения доступности catalog-команд.
 */
export const CATALOG_PAGE_IDS = ['catalog', 'used-cars'];

/**
 * Форматирует машину для агента (копия из commandExecutor.js)
 * @param {Object} car - Объект машины
 * @returns {Object} Отформатированный объект машины
 */
function formatCarForAgent(car) {
  return {
    id: car.id,
    title: carTitle(car),
    price: car.price,
    mileage: car.mileage,
    year: car.year,
    make: car.make,
    model: car.model,
    bodyStyle: car.bodyStyle || 'N/A',
    fuelType: car.fuelType || 'N/A',
    transmission: car.transmission || 'N/A',
    statusSold: car.statusSold || null,
    // The canonical delivery cell ('immediate' / 'YYYY-MM' / none, #1488). The agent
    // shows it as the car's arrival; without it a list of cars in transit went to the
    // customer as "10 available SUV matches".
    deliveryDate: deliveryCell(car.deliveryDate)
  };
}

/**
 * Listing fields sent as `currentCarListing` (#1487): everything a buyer asks about
 * the car on screen — price, availability, arrival, warranty, equipment, description.
 * The agent renders them into its car-on-screen block for every turn, so the car's
 * facts reach it with the message instead of by a lookup. Images, internal ids and
 * the pre-rendered `deliveryLabel` stay out. The agent's eval fixtures mirror this
 * list (evals/andys/fixtures/spec.py CAR_LISTING_FIELDS).
 */
export const CAR_LISTING_FIELDS = [
  'id', 'make', 'model', 'name', 'year', 'registrationMonth', 'price', 'oldPrice',
  'dutyFree', 'dutyFreePrice', 'mileage', 'statusSold', 'deliveryDate', 'warrantyTerm',
  'condition', 'conditionDescription', 'fuelType', 'transmission', 'drivetrain',
  'bodyStyle', 'doors', 'seats', 'color', 'exactColor', 'interiorColor',
  'interiorUpholstery', 'engineDisplacement', 'enginePower', 'co2Emissions',
  'registrationStatus', 'isHotOffer', 'features', 'description',
];

/**
 * The storefront listing of the car on screen, restricted to CAR_LISTING_FIELDS.
 * @param {string} carId - The car's id
 * @returns {Object|null} The listing, or null when the car is not loaded
 */
function getCarListing(carId) {
  const allCars = typeof window !== 'undefined' ? window.allCars : null;
  if (!carId || !Array.isArray(allCars)) {
    return null;
  }
  const car = allCars.find(c => c.id === carId);
  if (!car) {
    return null;
  }
  const listing = {};
  for (const field of CAR_LISTING_FIELDS) {
    if (car[field] !== undefined) {
      listing[field] = car[field];
    }
  }
  return listing;
}

/**
 * Получение информации об автомобиле из window.allCars
 * @param {string} carId - ID автомобиля
 * @returns {Object|null} Отформатированный объект машины или null
 */
function getCarData(carId) {
  if (!carId) {
    return null;
  }

  // Безопасное получение window.allCars (может быть undefined на начальном этапе)
  const allCars = typeof window !== 'undefined' ? window.allCars : null;

  if (!Array.isArray(allCars)) {
    return null;
  }

  const car = allCars.find(c => c.id === carId);

  if (!car) {
    return null;
  }

  // Возвращаем основные поля, релевантные для агента
  return {
    id: car.id,
    title: carTitle(car),
    make: car.make,
    model: car.model,
    year: car.year,
    price: car.price,
    mileage: car.mileage,
    bodyStyle: car.bodyStyle || null,
    fuelType: car.fuelType || null,
    transmission: car.transmission || null,
    color: car.color || null,
    isHotOffer: car.isHotOffer || false,
    statusSold: car.statusSold || null,
    // The car's own warranty term, with its unit (#1487). Without it the agent
    // answered warranty questions on a car page with the FAQ default.
    warrantyTerm: warrantyTermWithUnit(car.warrantyTerm),
    // #1488: the agent and its reply pills show it as the car's arrival. Only chat
    // messages carry `currentCarListing`, so after a navigation this is the one source.
    deliveryDate: deliveryCell(car.deliveryDate),
    hasDescription: !!car.description && car.description.trim().length > 0,
    hasFeatures: Array.isArray(car.features) && car.features.length > 0,
    imagesCount: Array.isArray(car.images) ? car.images.length : 0
  };
}

/**
 * Строит объект result из window.currentFilteredCars
 * @returns {Object|null} Объект с результатами фильтрации или null
 */
function buildResultObject() {
  const filteredData = window.currentFilteredCars;

  if (!filteredData || !Array.isArray(filteredData.cars)) {
    return null;
  }

  const { cars, total, allCarsTotal } = filteredData;
  const showing = Math.min(10, cars.length);

  // Вычисляем диапазоны цен и пробега
  const prices = cars.filter(c => c.price > 0).map(c => c.price);
  const mileages = cars.filter(c => c.mileage >= 0).map(c => c.mileage);

  const priceRange = prices.length > 0
    ? { min: Math.min(...prices), max: Math.max(...prices) }
    : null;

  const mileageRange = mileages.length > 0
    ? { min: Math.min(...mileages), max: Math.max(...mileages) }
    : null;

  return {
    total,
    allCarsTotal,
    showing,
    cars: cars.slice(0, showing).map(formatCarForAgent),
    visibleOfferIds: cars.map(c => c.id),
    priceRange,
    mileageRange
  };
}

/**
 * Resolve current website page from URL pathname + websitePages config from DB.
 * Returns {id, label, description} or null if no pages configured.
 */
export function resolveWebsitePage() {
  const pages = config.WEBSITE_PAGES || [];
  if (pages.length === 0) return null;

  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';

  // Match non-root pages first (sorted by path length desc = most specific first)
  const nonRootPages = pages
    .filter(p => p.id !== 'home' && p.path && p.path !== '/')
    .sort((a, b) => (b.path?.length || 0) - (a.path?.length || 0));

  for (const page of nonRootPages) {
    const pagePath = (page.path || '').replace(/\/+$/, '');
    if (pagePath && (pathname === pagePath || pathname.startsWith(pagePath + '/'))) {
      return { id: page.id, label: page.label, description: page.description || '' };
    }
  }

  // Default to home page
  const homePage = pages.find(p => p.id === 'home');
  if (homePage) {
    return { id: homePage.id, label: homePage.label, description: homePage.description || '' };
  }

  return null;
}

/**
 * Машина, на которую смотрит покупатель, когда виджет смонтирован на СТРАНИЦЕ
 * ДИЛЕРА (product page host-сайта, mini-car модалка, лендинг) — т.е. когда
 * машину нельзя выразить как route виджетового hash'а.
 *
 * `shiftgears:open` — единственный канал, через который проходят ВСЕ
 * dealer-page поверхности «открыть чат про эту машину»: SDK
 * (`window.ShiftGearsWidget.open` → `widget.jsx`) и прямые `CustomEvent`
 * fallback'и в `CarLandingPage` / `MiniCarModal` / `NewModelLanding` / Andys
 * landing. Тот же event слушает `VoiceWidgetConnected` (обработчик
 * `handleSdkOpen`), так что мы подписываемся на уже существующий контракт и
 * ничего не меняем в вызывающих файлах.
 *
 * Зачем latch: launch context ОДНОРАЗОВЫЙ (`clearLaunchContext()` после первого
 * сообщения), а page_context пересобирается на КАЖДОМ ходу. Без latch'а агент
 * называл машину в приветствии и через ход спрашивал «какая машина вас
 * интересует?» (#1251).
 *
 * Правило: побеждает ПОСЛЕДНИЙ open, включая open БЕЗ carId — он очищает latch.
 * Явное открытие чата, которое не называет машину, означает что разговор больше
 * не привязан к машине, а устаревшая машина в page_context — тот же класс бага,
 * что и отсутствующая.
 */
let launchContextCarId = null;

/**
 * @param {Event} event - CustomEvent `shiftgears:open`, detail = { mode, launchContext }
 */
function rememberLaunchContextCar(event) {
  const rawCarId = event?.detail?.launchContext?.metadata?.carId;
  launchContextCarId = rawCarId == null || rawCarId === '' ? null : String(rawCarId);
}

if (typeof window !== 'undefined') {
  window.addEventListener('shiftgears:open', rememberLaunchContextCar);
}

/**
 * Собирает полное состояние текущей страницы
 *
 * ВНИМАНИЕ (#1251): в этом объекте живут ДВЕ системы координат.
 * - Виджетовая (`window.location.hash`, HashRouter виджета): `route` /
 *   `widgetRoute`, `queryParams`, `activeFilters`.
 * - Host-страницы (`window.location`): `hostPath`, `websitePage`, `url`, `title`.
 * `route` — это НЕ «где находится пользователь» на сайте дилера; на dealer page
 * виджетовый hash обычно пустой. Ключи `widgetRoute` / `hostPath` добавлены
 * именно чтобы консьюмер не мог принять одну систему за другую. Существующие
 * ключи НЕ переименованы — их читает agent-side (кросс-репозиторный контракт).
 *
 * @param {Object} [options]
 * @param {boolean} [options.withCarListing=false] - add `currentCarListing` (#1487), only
 *   to the snapshot sent with a chat message: the agent builds its car block from it for
 *   that turn. Navigation and action events go without it, because they are stored in the
 *   database and reach the agent's context whole.
 * @returns {Object} Объект с контекстом страницы
 */
export function getCurrentPageContext({ withCarListing = false } = {}) {
  const hash = window.location.hash.substring(1); // Удаляем #
  const [pathname, search] = hash.split('?');
  const searchParams = new URLSearchParams(search || '');
  const widgetRoute = pathname || '/';

  // Построить result объект с топ-10 машинами (если данные есть)
  const result = buildResultObject();

  // Машина, которую смотрит покупатель: сначала собственный car-route виджета
  // (навигация внутри каталога), затем launch context страницы дилера (#1251) —
  // на product page дилера никакого `#/cars/<id>` для парсинга нет.
  const routeCarId = parseCurrentCarId(widgetRoute);
  const currentCarId = routeCarId || launchContextCarId;
  const currentCar = currentCarId ? getCarData(currentCarId) : null;
  const currentCarListing = withCarListing && currentCarId ? getCarListing(currentCarId) : null;

  return {
    route: widgetRoute,
    widgetRoute,
    hostPath: window.location.pathname,
    url: window.location.href,
    title: document.title,
    websitePage: resolveWebsitePage(),
    queryParams: Object.fromEntries(searchParams),
    activeFilters: parseActiveFilters(searchParams),
    filteredCarsCount: window.currentFilteredCars?.total || 0,
    totalCarsCount: window.currentFilteredCars?.allCarsTotal || 0,
    currentCarId,
    // Из какой системы координат пришёл currentCarId — иначе `route: '/cars'` +
    // `currentCarId: 'X'` читается как «пользователь на car-странице виджета».
    currentCarIdSource: routeCarId ? 'widget-route' : (currentCarId ? 'launch-context' : null),
    ...(currentCar && { currentCar }), // Добавить currentCar только если данные найдены
    ...(currentCarListing && { currentCarListing }), // #1487: the car's facts for the agent
    ...(result && { result }), // Добавить result только если данные есть
    // Organization and inventory IDs for agent context (#398)
    organizationId: config.ORGANIZATION_ID,
    inventoryId: config.INVENTORY_ID,
    timestamp: Date.now(),
  };
}

/**
 * Парсит активные фильтры из URL параметров
 * @param {URLSearchParams} searchParams
 * @returns {Object} Объект с активными фильтрами
 */
export function parseActiveFilters(searchParams) {
  const filters = {};

  // Категориальные фильтры (multi-select через запятую)
  ['make', 'model', 'bodyStyle', 'fuelType', 'transmission', 'color'].forEach(key => {
    const value = searchParams.get(key);
    if (value) {
      filters[key] = value.split(',');
    }
  });

  // Range фильтры для price
  const minPrice = searchParams.get('minPrice');
  const maxPrice = searchParams.get('maxPrice');
  if (minPrice || maxPrice) {
    filters.price = {};
    if (minPrice) filters.price.min = parseInt(minPrice);
    if (maxPrice) filters.price.max = parseInt(maxPrice);
  }

  // Range фильтры для mileage
  const minMileage = searchParams.get('minMileage');
  const maxMileage = searchParams.get('maxMileage');
  if (minMileage || maxMileage) {
    filters.mileage = {};
    if (minMileage) filters.mileage.min = parseInt(minMileage);
    if (maxMileage) filters.mileage.max = parseInt(maxMileage);
  }

  // Range фильтры для year
  const minYear = searchParams.get('minYear');
  const maxYear = searchParams.get('maxYear');
  if (minYear || maxYear) {
    filters.year = {};
    if (minYear) filters.year.min = parseInt(minYear);
    if (maxYear) filters.year.max = parseInt(maxYear);
  }

  // Сортировка
  const sortBy = searchParams.get('sortBy');
  if (sortBy) {
    filters.sortBy = sortBy;
  }

  return filters;
}

/**
 * Извлекает ID текущего автомобиля из route
 * @param {string} pathname
 * @returns {string|null} ID автомобиля или null
 */
function parseCurrentCarId(pathname) {
  // Подроуты машины — та же машина. Якорь конца строки здесь стоял с тех пор, когда
  // `/cars/<id>` был единственным car-route, и переживал #1251 как «сохраняем прежнее
  // поведение парсера» — то есть как храповик, а не как решение.
  //
  // Цена вскрылась 2026-09-18: покупатель открыл чат на `#/cars/311/landing`, перешёл на
  // `#/cars/309/landing` и спросил «what about this car» — а получил ответ про прежнюю.
  // Лендинг не матчился, `currentCarId` падал на латч `launchContextCarId`, который меняется
  // только на новый `shiftgears:open`, и якорь застревал на машине, с которой начали.
  //
  // Совпадает с агентской стороной (`engine/bind.py` `_CAR_ROUTE_PATTERN`), которая подроуты
  // распознаёт: расхождение двух парсеров и было тем зазором, куда протекал устаревший латч.
  const match = pathname.match(/^\/cars\/([^/]+)(?:\/|$)/);
  return match ? match[1] : null;
}
